import { MotifButton, MotifIcon } from '@ey-xd/motif-wc-react';
import { IconoirMenu } from '@ey-xd/motif-icon/Icons/Iconoir/Menu.js';
import { IconoirXmark } from '@ey-xd/motif-icon/Icons/Iconoir/Xmark.js';
import { IconoirViewGrid } from '@ey-xd/motif-icon/Icons/Iconoir/ViewGrid.js';
import { IconoirGroup } from '@ey-xd/motif-icon/Icons/Iconoir/Group.js';
import { IconoirClipboardCheck } from '@ey-xd/motif-icon/Icons/Iconoir/ClipboardCheck.js';
import { IconoirHelpCircle } from '@ey-xd/motif-icon/Icons/Iconoir/HelpCircle.js';
import { IconoirRefresh } from '@ey-xd/motif-icon/Icons/Iconoir/Refresh.js';
import { eventsModule } from '@/events/events-module';
import { useHeaderStore } from '@/header/header-store';
import { getTracker } from '@/telemetry/tracker';
import { openPopupMfe } from '@/mfe/popup-store';
import { invalidateLoaderCache } from '@/mfe/loader';
import { fullPortalRefresh } from '@/client/refresh-client-context';
import React from 'react';

/**
 * Icon-only action buttons in the header right slot. Each emits its
 * legacy event name (so the M2.9 shim and Angular host listen on the
 * same channel during canary) and emits telemetry for the D7 deprecation
 * dashboard.
 */
function IconButton({
  icon,
  label,
  onClick,
  stopPropagation = false,
  size = 23,
}: {
  icon: typeof IconoirMenu;
  label: string;
  onClick: () => void;
  stopPropagation?: boolean;
  size?: number;
}) {
  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    onClick();
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
  };
  return (
    <MotifButton
      variant="text"
      size="sm"
      shape="icon"
      aria-label={label}
      title={label}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      <MotifIcon src={icon} iconAriaLabel={label} fill="#ffffff" size={size} />
    </MotifButton>
  );
}

export function HamburgerButton({ open, onClick }: { open?: boolean; onClick: () => void }) {
  return (
    <IconButton
      icon={open ? IconoirXmark : IconoirMenu}
      label={open ? 'Close menu' : 'Open menu'}
      onClick={onClick}
    />
  );
}

export function WorkspacesButton() {
  return (
    <IconButton
      icon={IconoirViewGrid}
      label="Apps"
      stopPropagation
      onClick={() => {

        // Legacy `appsClicked` only toggles `showMenu`; it leaves the popup outlet
        // (orchestrator/chatbot MFE) untouched, so we do not close it here.
        eventsModule.emit('shell:open-workspaces');
      }}
    />
  );
}

/**
 * Orchestrator button — mirrors the legacy `ey-global-header`
 * `(orchestratorClicked)` checklist icon (`showOrchestratorIcon`). Legacy
 * `orchestratorClicked` navigated to the orchestrator MFE via the named
 * `popup` outlet (`router.navigate({ outlets: { popup: 'apps/orchestratoruitasks' } })`),
 * a contained slide-in overlay, NOT a full-page route. We mirror that by
 * opening it as the `orchestratoruitasks` popup MFE.
 */
export function OrchestratorButton() {
  return (
    <IconButton
      icon={IconoirClipboardCheck}
      label="Orchestrator"
      onClick={() => {

        openPopupMfe('orchestratoruitasks');
      }}
    />
  );
}

/**
 * Chat / assistant button — mirrors the legacy `ey-global-header`
 * `(chatBotClicked)` / `(eyqfsClicked)` icon.
 *
 * When `chatbotType` is `'chatbot'` the button opens the inline chatbot panel
 * (matching Angular's `header-chatbot` slide-in). Otherwise it opens the
 * MCP chatbot popup MFE (matching Angular's `header-eyqfs` → popup route).
 */
export function ChatBotButton({ chatbotType }: { chatbotType?: 'chatbot' | 'mcpChatbot' | '' }) {
  const togglePanel = useHeaderStore((s) => s.togglePanel);
  return (
    <MotifButton
      variant="text"
      size="sm"
      shape="icon"
      aria-label="Assistant"
      title="Assistant"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        getTracker().event('Header-chatbot-clicked');
        if (chatbotType === 'chatbot') {
          togglePanel('chatbot');
        } else {
          openPopupMfe('mcpchatbot');
        }
      }}
    >
      <svg
        width="35"
        height="35"
        viewBox="0 0 44 44"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M35.9333 21.8167C35.9333 27.4223 32.666 32.2648 27.9319 34.5437L21.8167 41.0667L21.8167 35.9334C14.0202 35.9334 7.7 29.6132 7.7 21.8167C7.7 14.0203 14.0202 7.70007 21.8167 7.70007C29.6131 7.70007 35.9333 14.0203 35.9333 21.8167ZM22.8283 14.6397C22.5763 13.92 21.5586 13.92 21.3066 14.6397L19.7948 18.9574C19.7171 19.1792 19.5465 19.3561 19.3276 19.4418L15.1198 21.0872C14.4365 21.3545 14.4365 22.3215 15.1198 22.5888L19.3276 24.2342C19.5465 24.3198 19.7171 24.4967 19.7948 24.7186L21.3066 29.0363C21.5586 29.756 22.5763 29.756 22.8283 29.0363L24.3401 24.7186C24.4178 24.4967 24.5885 24.3198 24.8074 24.2342L29.0151 22.5888C29.6985 22.3215 29.6985 21.3545 29.0151 21.0872L24.8074 19.4418C24.5885 19.3561 24.4178 19.1792 24.3401 18.9574L22.8283 14.6397Z"
          fill="white"
        />
      </svg>
    </MotifButton>
  );
}

/**
 * Group / people button — mirrors the legacy `ey-global-header`
 * `(groupClicked)` icon. Opens the "My Teams" slide-in panel.
 */
export function GroupButton() {
  const togglePanel = useHeaderStore((s) => s.togglePanel);
  return (
    <IconButton
      icon={IconoirGroup}
      label="My Teams"
      stopPropagation
      onClick={() => {

        // Legacy `groupClicked` only toggles `showMenu`; it leaves the popup outlet
        // (orchestrator/chatbot MFE) untouched, so we do not close it here.
        togglePanel('teams');
      }}
    />
  );
}

export function HelpButton() {
  return (
    <IconButton
      icon={IconoirHelpCircle}
      label="Help"
      stopPropagation
      onClick={() => {

        // Legacy `questionMarkClicked` only toggles `showMenu`; it leaves the popup
        // outlet (orchestrator/chatbot MFE) untouched, so we do not close it here.
        eventsModule.emit('shell:open-help');
      }}
    />
  );
}

/**
 * Refresh button — mirrors the legacy `(refreshClicked)` → `refreshClientList()`
 * flow. Delegates to `fullPortalRefresh()` which replicates the full Angular
 * chain: cache clear, RUM token refresh, role re-resolution, screen access
 * check, tile provisioning, navigation, toasts, and MFE remount.
 */
export function RefreshButton() {
  return (
    <IconButton
      icon={IconoirRefresh}
      label="Refresh"
      size={18}
      onClick={() => {
        getTracker().event('Header-refresh-clicked');
        invalidateLoaderCache();
        void fullPortalRefresh();
      }}
    />
  );
}
